/**
 * Statistics Card Component
 * Displays session statistics with visual indicators
 */

import { getSignificanceText, normalizeSignificance } from '../api/firebase';

interface Statistics {
  totalBits?: number; // Optional - may be missing in old data
  ones: number;
  zeros: number;
  zScore: number;
  pValue: number;
  significance: string;
  effectSizeP?: number;
  anomalyScore?: number;
}

/**
 * Render the statistics card
 */
export function renderStatistics(
  statistics: Statistics,
  container: HTMLElement
): void {
  const {
    ones,
    zeros,
    zScore,
    pValue,
    significance,
    effectSizeP,
    anomalyScore,
  } = statistics;

  // Add fallback for totalBits
  const totalBits = statistics.totalBits || (ones || 0) + (zeros || 0);
  if (totalBits === 0) {
    console.warn('Statistics has no totalBits');
  }

  // Calculate percentages
  const onesPercent = totalBits > 0 ? (ones / totalBits * 100) : 50;
  const zerosPercent = 100 - onesPercent;

  // Determine z-score color
  const zScoreClass = Math.abs(zScore) > 1.96 ? 'stat-positive' : '';

  container.innerHTML = `
    <div class="card">
      <h3 class="card-title">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="20" x2="18" y2="10"></line>
          <line x1="12" y1="20" x2="12" y2="4"></line>
          <line x1="6" y1="20" x2="6" y2="14"></line>
        </svg>
        Session Statistics
      </h3>

      <!-- Significance Badge -->
      <div style="text-align: center; margin-bottom: 16px;">
        <span class="significance-badge significance-${normalizeSignificance(significance)}">
          ${getSignificanceText(significance)}
        </span>
      </div>

      <!-- Statistics Grid -->
      <div class="statistics-grid">
        <div class="stat-item">
          <div class="stat-value">${formatNumber(totalBits)}</div>
          <div class="stat-label">Total Bits</div>
        </div>

        <div class="stat-item">
          <div class="stat-value ${zScoreClass}">${zScore.toFixed(3)}</div>
          <div class="stat-label">Z-Score</div>
        </div>

        <div class="stat-item">
          <div class="stat-value">${formatPValue(pValue)}</div>
          <div class="stat-label">P-Value</div>
        </div>

        ${effectSizeP !== undefined ? `
        <div class="stat-item">
          <div class="stat-value">${(effectSizeP * 100).toFixed(1)}%</div>
          <div class="stat-label">Effect Size</div>
        </div>
        ` : ''}
      </div>

      <!-- Bit Distribution -->
      <div style="margin-top: 20px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 14px;">
          <span style="color: #60a5fa;">Ones: ${formatNumber(ones)} (${onesPercent.toFixed(1)}%)</span>
          <span style="color: #f97316;">Zeros: ${formatNumber(zeros)} (${zerosPercent.toFixed(1)}%)</span>
        </div>
        <div style="display: flex; height: 8px; border-radius: 4px; overflow: hidden;">
          <div style="width: ${onesPercent}%; background: #60a5fa;"></div>
          <div style="width: ${zerosPercent}%; background: #f97316;"></div>
        </div>
      </div>

      ${anomalyScore !== undefined && anomalyScore > 0.5 ? `
      <div style="margin-top: 16px; padding: 12px; background: rgba(167, 139, 250, 0.2); border-radius: 8px; text-align: center;">
        <span style="color: var(--color-accent-light); font-weight: 600;">
          Anomaly Score: ${(anomalyScore * 100).toFixed(0)}%
        </span>
      </div>
      ` : ''}
    </div>
  `;
}

/**
 * Format large numbers with commas
 */
function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * Format p-value in scientific notation if needed
 */
function formatPValue(pValue: number): string {
  if (pValue < 0.001) {
    return pValue.toExponential(2);
  }
  return pValue.toFixed(4);
}
